import type { DataTableType } from '@/components/DataTable/constants/dataTableConstants';

/**
 * Centralised React Query cache key factory for the dashboard app.
 *
 * Keeps all manually-defined query keys in one place so that mutations
 * targeting the same cache entries as their corresponding queries always
 * stay in sync. Orval-generated keys (e.g. getGetSdkTokenQueryKey) are
 * already centralised and do not need to be repeated here.
 */

const ANSWER_DATA_DEFAULTS = { recordOffset: 0, recordSize: -1 } as const;

export const queryKeys = {
  answerData: {
    /**
     * Matches the cache entry created by useAnswerDataQuery when called with
     * default pagination (recordOffset=0, recordSize=-1). Use this in
     * mutations that need to read, write, or cancel the default table query.
     */
    defaults: (answerId: string, dataType: DataTableType, region: string) =>
      [
        'answerData',
        answerId,
        dataType,
        ANSWER_DATA_DEFAULTS.recordOffset,
        ANSWER_DATA_DEFAULTS.recordSize,
        region,
      ] as const,

    /**
     * Full paginated key. Use this inside useAnswerDataQuery itself where
     * the exact offset and size are known.
     */
    paginated: (answerId: string, dataType: DataTableType, recordOffset: number, recordSize: number, region: string) =>
      ['answerData', answerId, dataType, recordOffset, recordSize, region] as const,
  },

  jobStatus: {
    byId: (jobId: string | null) => ['jobStatus', jobId] as const,
  },

  thoughtspotBanner: {
    byRegion: (region: string) => ['thoughtspot-banner', region] as const,
  },

  previewSurveyMerge: {
    byIds: (accountId: number, surveyIds: number[]) => ['previewSurveyMerge', accountId, surveyIds] as const,
  },

  shareUsers: {
    byObjectId: (objectId: string) => ['shareUsers', objectId] as const,
  },
} as const;
